import type { OrderSupplyAllocation, OrderSupplyLine } from '../order.service';

const quantity = (value: number) => Math.round(Number(value || 0) * 100) / 100;

/** Only explicit allocation receipt facts establish supplier receipt; a local reserve does not. */
export function orderSupplierReceipt(allocation: Pick<OrderSupplyAllocation, 'quantity'> & Partial<Pick<
  OrderSupplyAllocation, 'received_quantity' | 'physically_received_quantity' | 'physical_unreconciled_quantity'
>>) {
  const nonnegative = (value: number | undefined) =>
    Number.isFinite(Number(value)) ? Math.max(Number(value), 0) : 0;
  const total = quantity(nonnegative(allocation.quantity));
  const documentary = Math.min(total, quantity(nonnegative(allocation.received_quantity)));
  // physically_received_quantity is a total, not an increment over documentary receipt.
  const physical = allocation.physically_received_quantity == null
    ? documentary + nonnegative(allocation.physical_unreconciled_quantity)
    : nonnegative(allocation.physically_received_quantity);
  const received = quantity(Math.min(total, Math.max(documentary, physical)));
  return {
    received,
    unreconciled: quantity(Math.max(received - documentary, 0)),
    open: quantity(Math.max(total - received, 0)),
    complete: total > 0 && received >= total
  };
}

export function orderSupplyCoverage(
  required: number,
  line: Pick<
    OrderSupplyLine,
    | 'reserved'
    | 'fulfilled'
    | 'transfer_pending'
    | 'transfer_reserved'
    | 'transfer_in_transit'
    | 'allocations'
  >,
  supplierDraftQuantity: number,
  warehouseDraftQuantity: number
) {
  const orderedOpen = line.allocations
    .filter((row) => row.status === 'ORDERED' || row.status === 'PARTIALLY_RECEIVED')
    .reduce(
      (sum, row) => sum + orderSupplierReceipt(row).open,
      0
    );
  // Picked stock is part of reserved; received goods are already in local coverage.
  // transfer_pending includes both reserved and in-transit transfers, even without a document.
  const transferPending =
    line.transfer_pending ??
    Number(line.transfer_reserved || 0) + Number(line.transfer_in_transit || 0);
  const committed = quantity(
    Number(line.fulfilled || 0) + Number(line.reserved || 0) + Number(transferPending) + orderedOpen
  );
  const covered = quantity(committed + supplierDraftQuantity + warehouseDraftQuantity);
  const difference = quantity(covered - required);
  return {
    required: quantity(required),
    committed,
    covered,
    difference,
    remaining: Math.max(-difference, 0),
    status: Math.abs(difference) < 0.001 ? 'complete' : difference < 0 ? 'under' : 'over'
  } as const;
}

// A retry after a lost response must carry the same id until the actual payload changes.
export class OrderSaveRequestIdentity {
  private signature = '';
  private requestId = '';
  private uncertainSignature: string | undefined;

  markAttemptSent(payload: unknown) {
    this.uncertainSignature = JSON.stringify(payload);
  }

  acknowledgeResult() {
    this.uncertainSignature = undefined;
  }

  canRetryUncertainPayload(payload: unknown) {
    return this.uncertainSignature === JSON.stringify(payload);
  }

  forPayload(payload: unknown, createId: () => string): string {
    const signature = JSON.stringify(payload);
    if (signature !== this.signature || !this.requestId) {
      this.signature = signature;
      this.requestId = createId();
    }
    return this.requestId;
  }
}
